import React, { useEffect, useState, useContext, useRef } from "react";
import * as Icons from "@mui/icons-material/";
import { Route, Switch, Link, useHistory } from "react-router-dom";
import * as signalR from "@microsoft/signalr";
import { HubConnectionState } from "@microsoft/signalr";

import {
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Menu,
    MenuItem,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    InputBase,
    Badge,
    Popover,
    Tooltip,
    Button,
    Divider,
    Dialog,
    DialogTitle,
    DialogContentText,
    DialogContent,
    DialogActions,
    useMediaQuery,
    Grid,
} from "@material-ui/core";
import { useTheme, createStyles, makeStyles, Theme, fade } from "@material-ui/core/styles";
import {
    PowerSettingsNew,
    AccountCircle,
    Settings,
    Notifications,
    Search,
    Visibility,
    SettingsSystemDaydream,
    Ballot,
    Home,
    Archive,
    People,
    Person,
    Assessment,
    Translate,
    Build,
    ThreeDRotation,
} from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { Profile } from "oidc-client";
import { useSnackbar } from "notistack";
import BDTLogo from "../../../../assets/images/bdt_logo.svg";
import TelefonicaLogoWhite from "../../../../assets/images/telefonica_logo_white.svg";
import {
    IndexModel,
    MarkAsReadCommand,
    ProjectDto,
    UserNotificationDto,
} from "../../../api/APIClient";
import { BDTRoutes } from "../../../helpers/applicationConstants";
import { notificationsService } from "../../../api/Services";
import { ApplicationPaths } from "../../../auth/ApiAuthorizationConstants";
import { authService } from "../../../auth/AuthorizeService";
import i18n from "../../../../i18n";

import PanelProjectContext from "../../PanelProjectProvider";
import { usePureEffect } from "../../../helpers/usePureEffect";
import PanelViewerContext from "../../../Context/PanelViewerProvider";
import useAsset from "../../Project/Viewer/hooks/useAsset";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: "flex",
        },
        appBar: {
            zIndex: theme.zIndex.drawer + 2,
            background: "linear-gradient(35deg, rgba(11,39,57,0.9) 0%, rgba(0,102,255,1) 55%)",
            // backgroundColor: theme.palette.background.default,
            boxShadow: `0  -2px 6px 0px ${theme.palette.secondary.main}`,
        },
        toolBar: {
            justifyContent: "space-between",
        },
        toolBarActions: {
            display: "flex",
            flexDirection: "row",
        },
        menuButton: {
            color: theme.palette.background.default,
        },
        search: {
            position: "relative",
            borderRadius: theme.shape.borderRadius,
            backgroundColor: fade(theme.palette.secondary.light, 0.35),
            "&:hover": {
                backgroundColor: fade(theme.palette.secondary.light, 0.55),
            },
            marginLeft: 0,
            width: "100%",
            [theme.breakpoints.up("sm")]: {
                marginLeft: theme.spacing(1),
                width: "auto",
            },
            [theme.breakpoints.down("xs")]: {
                display: "none",
            },
        },
        searchIcon: {
            padding: theme.spacing(0, 2),
            height: "100%",
            position: "absolute",
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        inputRoot: {
            color: "inherit",
        },
        inputInput: {
            padding: theme.spacing(1, 1, 1, 0),
            paddingLeft: `calc(1em + ${theme.spacing(4)}px)`,
            transition: theme.transitions.create("width"),
            width: "100%",
            [theme.breakpoints.up("sm")]: {
                width: "12ch",
                "&:focus": {
                    width: "20ch",
                },
            },
        },
        userMenuItemIcon: {
            paddingLeft: 10,
        },
        typography: {
            padding: theme.spacing(2),
        },
        logo: {
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
        },
        dev: {
            position: "absolute",
            width: "100%",
            height: "80%",
            backgroundImage: "url(https://cdn-icons-png.flaticon.com/512/126/126428.png)",
            backgroundSize: "contain",
            backgroundRepeat: "space",
            opacity: 0.035,
            zIndex: -1,
        },
    })
);

const notificationsConnection = new signalR.HubConnectionBuilder()
    .withUrl("/hubs/notifications", {
        accessTokenFactory: async () => {
            const token = await authService.getAccessToken();
            return token !== null ? token : "";
        },
        withCredentials: true,
    })
    .configureLogging(signalR.LogLevel.Error)
    .withAutomaticReconnect()
    .build();

interface IPanelProjectContext {
    setProject: React.Dispatch<React.SetStateAction<ProjectDto>>;
    project: ProjectDto;
    setNotifications: React.Dispatch<React.SetStateAction<UserNotificationDto[]>>;
    notifications: UserNotificationDto[];
    setNotificationsCount: React.Dispatch<React.SetStateAction<number>>;
    notificationsCount: number;
    isViewerReady: boolean;
    setView: React.Dispatch<React.SetStateAction<string>>;
    setAssetSelection: React.Dispatch<React.SetStateAction<IndexModel | undefined>>;
    setIsRightPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setIsWorkOrderOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setIsRenderingItems: React.Dispatch<React.SetStateAction<boolean>>;
}

const TopBar = (): JSX.Element => {
    const history = useHistory();

    const { enqueueSnackbar } = useSnackbar();

    const { t } = useTranslation("main");

    const {
        project,
        notifications,
        setNotifications,
        notificationsCount,
        setNotificationsCount,
        isViewerReady,
        setView,
        setAssetSelection,
        setIsRightPanelOpen,
        setIsWorkOrderOpen,
        setIsRenderingItems,
    } = React.useContext<IPanelProjectContext>(PanelProjectContext);

    // Esta es una solucion temporal para extraer el projectId de la url

    // const urlActual = window.location.pathname;
    // const arrPaths = urlActual.split("/");
    // const [projectID, setProjectId] = useState(arrPaths[3]);

    // useEffect(() => {
    //     setProjectId(arrPaths[3]);
    // }, [urlActual]);

    // Fin de la solucion temporal

    const classes = useStyles();

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));
    const [popUp, setPopUp] = useState(false);
    const [isDev, setIsDev] = useState<boolean>(true);
    const togglePopup = () => {
        setPopUp(!popUp);
    };
    const [notificationsHistoryList, setNotificationsHistoryList] = useState<UserNotificationDto[]>(
        []
    );
    const getNotificationsHistoryList = async () => {
        notificationsService
            .getAllNotificationsByUser(project.id)
            .then((res) => {
                setNotificationsHistoryList(res.data);
            })
            .catch((err) => {
                enqueueSnackbar(`${err.errorDescription.message}`, {
                    variant: "error",
                    anchorOrigin: {
                        horizontal: "right",
                        vertical: "bottom",
                    },
                });
                console.error(err);
            });
    };

    const heightCalc = window.innerHeight - 400;

    // const [notificationsCount, setNotificationsCount] = useState(0);
    // const [notifications, setNotifications] = useState<UserNotificationDto[]>([]);
    const [anchorElPopOver, setAnchorElPopOver] = useState<HTMLButtonElement | null>(null);
    const [profile, setProfile] = useState<Profile | null>(null);

    const handleClickPopOver = (event: React.MouseEvent<HTMLButtonElement>) => {
        notificationsService.getUnreadNotifications(project.id).then((res) => {
            if (res && res.data) {
                setNotifications(res.data);
                // setNotificationsCount(res.data.map((x) => x.ids.split(",")).length);
                setNotificationsCount(res.data.length);
            } else {
                setNotifications([]);
                setNotificationsCount(0);
            }
        });
        setAnchorElPopOver(event.currentTarget);
    };

    const handleClosePopOver = () => {
        setAnchorElPopOver(null);
    };

    const openPopOver = Boolean(anchorElPopOver);
    const idPopOver = openPopOver ? "simple-popover" : undefined;

    const [assetIdCommentsSelected, setAssetIdCommentsSelected] = useState<string | undefined>(
        undefined
    );

    const handleCommentsPanelItemClick = (route: string, asset: string | undefined) => {
        if (!isViewerReady) history.push(route);
        console.log(`accedo al evento con el asset ${asset}`);
        console.log(`isViewerReady ${isViewerReady}`);
        console.log(`assetIdCommentsSelected-antes ${assetIdCommentsSelected}`);
        handleClosePopOver();
        setAssetIdCommentsSelected(asset);
    };

    const { fetchingAssetInformationById } = useAsset();

    useEffect(
        function () {
            console.log(`assetIdCommentsSelected-despues ${assetIdCommentsSelected}`);
            if (isViewerReady && assetIdCommentsSelected !== undefined) {
                fetchingAssetInformationById(project.id, assetIdCommentsSelected);
                setAssetIdCommentsSelected(undefined);
                setView("chat");
            }
        },
        [isViewerReady, assetIdCommentsSelected]
    );

    const subscribeToNotificationsSignalR = async (updatedProjectId) => {
        authService.getProfile().then((profResponse) => {
            if (profResponse !== null) {
                if (notificationsConnection.state === HubConnectionState.Disconnected)
                    setProfile(profResponse);
                notificationsConnection
                    .start()
                    .then(() => {
                        authService.getProfile().then((prof) => {
                            if (prof !== null) {
                                notificationsConnection.invoke("AddUserConnection", prof.oid);
                                notificationsConnection.on(
                                    "PushNotifications",
                                    (theNewProjectId) => {
                                        notificationsService
                                            .getUnreadNotificationsCount(theNewProjectId)
                                            .then((res) => {
                                                setNotificationsCount(res.data.number);
                                            })
                                            .catch((err) => {
                                                enqueueSnackbar(`${err.errorDescription.message}`, {
                                                    variant: "error",
                                                    anchorOrigin: {
                                                        horizontal: "right",
                                                        vertical: "bottom",
                                                    },
                                                });
                                                console.error(err);
                                            });
                                    }
                                );
                            }
                        });
                    })
                    .catch((err) => {
                        enqueueSnackbar(`${err}`, {
                            variant: "error",
                            anchorOrigin: {
                                horizontal: "right",
                                vertical: "bottom",
                            },
                        });
                        console.error(err);
                    })
                    .finally(() => {
                        authService.getProfile().then((prof) => {
                            if (prof !== null) {
                                notificationsService
                                    .getUnreadNotificationsCount(project.id)
                                    .then((res) => {
                                        setNotificationsCount(res.data.number);
                                    })
                                    .catch((err) => {
                                        enqueueSnackbar(`${err}`, {
                                            variant: "error",
                                            anchorOrigin: {
                                                horizontal: "right",
                                                vertical: "bottom",
                                            },
                                        });
                                        console.error(err);
                                    });
                            }
                        });
                    });
            }
        });
    };

    useEffect(() => {
        const dev =
            window.location.host.includes("localhost") || window.location.host.includes("5050");
        setIsDev(dev);
    }, []);

    usePureEffect(() => {
        setView("");
        setAssetSelection({});
        setIsRightPanelOpen(false);
        setIsWorkOrderOpen(false);
        setIsRenderingItems(false);
        if (notificationsConnection.state === HubConnectionState.Connected) {
            notificationsConnection.stop().then(() => {
                subscribeToNotificationsSignalR(project);
            });
        } else {
            subscribeToNotificationsSignalR(project);
        }
    }, [project]);

    const [anchorProfile, setAnchorProfile] = React.useState<null | HTMLElement>(null);
    const handleClickProfile = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorProfile(event.currentTarget);
    };
    const handleCloseProfile = () => {
        setAnchorProfile(null);
    };

    const [anchorSettings, setAnchorSettings] = React.useState<null | HTMLElement>(null);
    const handleClickSettings = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorSettings(event.currentTarget);
    };
    const handleCloseSettings = () => {
        setAnchorSettings(null);
    };

    const handleMenuItemClick = (route: string) => {
        history.push(route);
        handleCloseSettings();
        handleCloseProfile();
    };

    const [anchorLanguages, setAnchorLanguages] = React.useState<null | HTMLElement>(null);
    const handleClickLanguages = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorLanguages(event.currentTarget);
    };
    const handleCloseLanguages = () => {
        setAnchorLanguages(null);
    };

    const handleMenuLanguageClick = (langSelected: string) => {
        i18n.changeLanguage(langSelected);
        // TODO set user language on server
        handleCloseLanguages();
    };

    const handleLogOut = () => {
        authService.getProfile().then((prof) => {
            if (prof !== null) {
                notificationsConnection
                    .invoke("RemoveUserConnection", prof.sub)
                    .then(() => {
                        history.push(ApplicationPaths.LogOut, { local: true });
                        handleCloseSettings();
                        handleCloseProfile();
                    })
                    .catch((error) => {});
            }
        });
    };

    const handleReadNotification = async (notId, projectid) => {
        if (notId) {
            const newNotId = notId.split(",").map((x) => Number(x));
            const command: MarkAsReadCommand = { ids: newNotId, projectId: projectid };
            await notificationsService.markAsRead(command);
            notificationsService.getUnreadNotifications(projectid).then((res) => {
                if (res) {
                    setNotifications(res.data);
                    setNotificationsCount(res.data.length);
                } else {
                    setNotifications([]);
                    setNotificationsCount(0);
                }
            });
        }
    };

    return (
        <div className={classes.root}>
            <AppBar className={classes.appBar} position="fixed" color="secondary">
                <Toolbar className={classes.toolBar}>
                    <div className={classes.search}>
                        <div className={classes.searchIcon}>
                            <Search />
                        </div>
                        <InputBase
                            fullWidth
                            placeholder={t("topbar.search")}
                            classes={{
                                root: classes.inputRoot,
                                input: classes.inputInput,
                            }}
                            inputProps={{ "aria-label": "search" }}
                        />
                    </div>

                    <div className={classes.logo}>
                        <Link to={BDTRoutes.Home}>
                            <img height="32" src={TelefonicaLogoWhite} alt="Telefónica logo" />
                        </Link>
                    </div>

                    {isDev && <div className={classes.dev} />}

                    <div className={classes.toolBarActions}>
                        <Link to={BDTRoutes.Home}>
                            <IconButton className={classes.menuButton}>
                                <Home />
                            </IconButton>
                        </Link>
                        <IconButton onClick={handleClickLanguages} className={classes.menuButton}>
                            <Translate />
                        </IconButton>
                        <Menu
                            anchorEl={anchorLanguages}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            open={Boolean(anchorLanguages)}
                            onClose={handleCloseLanguages}
                            autoFocus={false}
                        >
                            <MenuItem onClick={() => handleMenuLanguageClick("es")}>
                                <Typography variant="body1">{t("languages.spanish")}</Typography>
                            </MenuItem>
                            <MenuItem onClick={() => handleMenuLanguageClick("en")}>
                                <Typography variant="body1">{t("languages.english")}</Typography>
                            </MenuItem>
                            <MenuItem onClick={() => handleMenuLanguageClick("fr")}>
                                <Typography variant="body1">{t("languages.french")}</Typography>
                            </MenuItem>
                        </Menu>
                        {/* <IconButton onClick={handleClickSettings} className={classes.menuButton}>
                            <Settings />
                        </IconButton>
                        <Menu
                            anchorEl={anchorSettings}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            open={Boolean(anchorSettings)}
                            onClose={handleCloseSettings}
                            autoFocus={false}
                        >
                            <ListItem dense divider>
                                <ListItemText secondary={t("topbar.forge")} />
                            </ListItem>
                            <MenuItem
                                onClick={() => {
                                    handleMenuItemClick(BDTRoutes.Administration.ForgeAccounts);
                                }}
                            >
                                <SettingsSystemDaydream />
                                <span className={classes.userMenuItemIcon}>
                                    {t("topbar.forgeAccountsAdmin")}
                                </span>
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    handleMenuItemClick(BDTRoutes.Administration.Models);
                                }}
                            >
                                <Archive />
                                <span className={classes.userMenuItemIcon}>
                                    {t("topbar.modelsAdmin")}
                                </span>
                            </MenuItem>
                            <ListItem dense divider>
                                <ListItemText secondary={t("topbar.powerBI")} />
                            </ListItem>
                            <MenuItem
                                onClick={() => {
                                    handleMenuItemClick(BDTRoutes.Administration.PowerBIAccounts);
                                }}
                            >
                                <Assessment />
                                <span className={classes.userMenuItemIcon}>
                                    {t("topbar.powerBIAccountsAdmin")}
                                </span>
                            </MenuItem>
                            <ListItem dense divider>
                                <ListItemText secondary={t("topbar.projects")} />
                            </ListItem>
                            <MenuItem
                                onClick={() => {
                                    handleMenuItemClick(BDTRoutes.Administration.Projects);
                                }}
                            >
                                <Ballot />
                                <span className={classes.userMenuItemIcon}>
                                    {t("topbar.projectsAdmin")}
                                </span>
                            </MenuItem>
                        </Menu> */}

                        <IconButton
                            onClick={(event) => {
                                handleClickPopOver(event);
                            }}
                            className={classes.menuButton}
                        >
                            <Badge badgeContent={notificationsCount} color="primary">
                                <Notifications />
                            </Badge>
                        </IconButton>
                        <Popover
                            id={idPopOver}
                            open={openPopOver}
                            anchorEl={anchorElPopOver}
                            onClose={handleClosePopOver}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            PaperProps={{
                                style: { width: "25%" },
                            }}
                        >
                            {notificationsCount > 0 ? (
                                <List>
                                    {notifications
                                        .sort(
                                            (a, b) =>
                                                new Date(b.created).getTime() -
                                                new Date(a.created).getTime()
                                        )
                                        .map((not) => (
                                            <ListItem key={not.ids} divider>
                                                <Tooltip
                                                    title={t("topbar.markAsRead").toString()}
                                                    arrow
                                                >
                                                    <ListItemIcon
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => {
                                                            handleReadNotification(
                                                                not.ids,
                                                                project.id
                                                            );
                                                        }}
                                                    >
                                                        {not.read
                                                            ? React.createElement(
                                                                  Icons.MarkEmailRead,
                                                                  {
                                                                      color: "disabled",
                                                                  }
                                                              )
                                                            : React.createElement(
                                                                  Icons.MarkEmailUnread
                                                              )}
                                                    </ListItemIcon>
                                                </Tooltip>
                                                <ListItemText
                                                    primary={not.title}
                                                    secondary={
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                flexDirection: "row",
                                                                justifyContent: "space-between",
                                                            }}
                                                        >
                                                            <ListItemText
                                                                style={{
                                                                    fontSize: "inherit",
                                                                    width: "60%",
                                                                    height: "100%",
                                                                }}
                                                                primary={not.description}
                                                                // secondary={`${React.createElement(
                                                                //     Icons.QueryBuilder
                                                                // )} ${
                                                                //     not.created
                                                                //         .toString()
                                                                //         .split("T")[0]
                                                                // } ${
                                                                //     not.created
                                                                //         .toString()
                                                                //         .split("T")[1]
                                                                //         .split(".")[0]
                                                                // }`}
                                                                secondary={
                                                                    <div
                                                                        style={{
                                                                            display: "flex",
                                                                        }}
                                                                    >
                                                                        {not.titleParams ? (
                                                                            <Button
                                                                                style={{
                                                                                    fontSize:
                                                                                        "xx-small",
                                                                                    marginTop:
                                                                                        "auto",
                                                                                    marginBottom:
                                                                                        "auto",
                                                                                }}
                                                                                variant="outlined"
                                                                                color="primary"
                                                                                onClick={() => {
                                                                                    handleCommentsPanelItemClick(
                                                                                        BDTRoutes.Project.Viewer.Viewer.Builder(
                                                                                            project.id
                                                                                        ),
                                                                                        not.titleParams
                                                                                    );
                                                                                }}
                                                                            >
                                                                                Acceder al panel
                                                                            </Button>
                                                                        ) : null}
                                                                        <h3
                                                                            style={{
                                                                                fontSize: "x-small",
                                                                                // force text to be aligned to the right
                                                                                marginLeft: "auto",
                                                                            }}
                                                                        >
                                                                            {
                                                                                not.created
                                                                                    .toString()
                                                                                    .split("T")[0]
                                                                            }{" "}
                                                                            {
                                                                                not.created
                                                                                    .toString()
                                                                                    .split("T")[1]
                                                                                    .split(".")[0]
                                                                            }
                                                                        </h3>
                                                                    </div>
                                                                }
                                                            />
                                                        </div>
                                                    }
                                                />
                                            </ListItem>
                                        ))}
                                </List>
                            ) : (
                                <Typography className={classes.typography}>
                                    {t("topbar.noNotifications")}
                                </Typography>
                            )}
                            <ListItemText
                                primary={
                                    notificationsCount > 0 ? null : <Divider variant="fullWidth" />
                                }
                                secondary={
                                    <Button
                                        style={{
                                            scale: "70%",
                                            minWidth: "-webkit-fill-available",
                                            top: "center",
                                        }}
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => {
                                            togglePopup();
                                            getNotificationsHistoryList();
                                            handleClosePopOver();
                                        }}
                                    >
                                        Ver historial de notificaciones
                                    </Button>
                                }
                            />
                        </Popover>
                        {popUp ? (
                            <>
                                <Dialog
                                    open={popUp}
                                    onClose={togglePopup}
                                    fullScreen={fullScreen}
                                    fullWidth={true}
                                    style={{
                                        maxHeight: "100%",
                                    }}
                                    maxWidth="xs"
                                >
                                    <DialogTitle>Historial de notificaciones</DialogTitle>
                                    <DialogContent>
                                        {notificationsHistoryList.length > 0 ? (
                                            <List>
                                                {notificationsHistoryList
                                                    .sort(
                                                        (a, b) =>
                                                            new Date(b.created).getTime() -
                                                            new Date(a.created).getTime()
                                                    )
                                                    .map((not) => (
                                                        <ListItem key={not.ids} divider>
                                                            <ListItemIcon
                                                                style={{ cursor: "pointer" }}
                                                                onClick={async () => {
                                                                    await handleReadNotification(
                                                                        not.ids,
                                                                        project.id
                                                                    );
                                                                    await getNotificationsHistoryList();
                                                                }}
                                                            >
                                                                {not.read
                                                                    ? React.createElement(
                                                                          Icons.MarkEmailRead,
                                                                          { color: "disabled" }
                                                                      )
                                                                    : React.createElement(
                                                                          Icons.MarkEmailUnread
                                                                      )}
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={not.title}
                                                                secondary={
                                                                    <div
                                                                        style={{
                                                                            display: "flex",
                                                                            flexDirection: "row",
                                                                            justifyContent:
                                                                                "space-between",
                                                                        }}
                                                                    >
                                                                        <ListItemText
                                                                            style={{
                                                                                fontSize: "inherit",
                                                                                width: "60%",
                                                                                height: "100%",
                                                                            }}
                                                                            primary={
                                                                                not.description
                                                                            }
                                                                            secondary={
                                                                                <div
                                                                                    style={{
                                                                                        display:
                                                                                            "block",
                                                                                        textAlign:
                                                                                            "right",
                                                                                        WebkitTextStroke:
                                                                                            "thin",
                                                                                    }}
                                                                                >
                                                                                    {
                                                                                        not.created
                                                                                            .toString()
                                                                                            .split(
                                                                                                "T"
                                                                                            )[0]
                                                                                    }{" "}
                                                                                    {
                                                                                        not.created
                                                                                            .toString()
                                                                                            .split(
                                                                                                "T"
                                                                                            )[1]
                                                                                            .split(
                                                                                                "."
                                                                                            )[0]
                                                                                    }
                                                                                </div>
                                                                            }
                                                                        />
                                                                    </div>
                                                                }
                                                            />
                                                        </ListItem>
                                                    ))}
                                            </List>
                                        ) : (
                                            "No hay notificaciones"
                                        )}
                                    </DialogContent>
                                </Dialog>
                            </>
                        ) : null}

                        <IconButton onClick={handleClickProfile} className={classes.menuButton}>
                            <AccountCircle />
                        </IconButton>
                        <Menu
                            anchorEl={anchorProfile}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            open={Boolean(anchorProfile)}
                            onClose={handleCloseProfile}
                        >
                            {/* TODO User profile */}
                            {/* <MenuItem onClick={() => handleMenuItemClick(BDTRoutes)}>
                                <Person />{" "}
                                <span className={classes.userMenuItemIcon}>{t("profile")}</span>
                            </MenuItem> */}
                            <MenuItem>
                                <span className={classes.userMenuItemIcon}>
                                    {profile?.name ? profile.name : "Usuario desconocido"}
                                </span>
                            </MenuItem>
                            <MenuItem onClick={handleLogOut}>
                                <PowerSettingsNew />
                                <span className={classes.userMenuItemIcon}>
                                    {t("topbar.logout")}
                                </span>
                            </MenuItem>
                        </Menu>
                    </div>
                </Toolbar>
            </AppBar>
        </div>
    );
};

const TopBarM = React.memo(TopBar);

export default TopBarM;
